import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  TabContent,
  TabPane,
  Form,
  Row,
  Col,
  FormGroup,
  Label,
  CustomInput,
} from 'reactstrap';
import {
  ContAffiliation,
  H5Styled,
  ContFilterYear,
} from '../../../styled-components';
import ProvidersTable from './ProvidersTable';
import AdministratorsTable from './AdministratorsTable';
import Summary from './Summary';
import TotalsRow from './TotalTr';
import TotalRevenuesRow from './TotalTrRevenue';
import TotalInsuranceRevenuesRow from './TotalTrInsuranceRevenue';
import RoyaltyTable from './RoyaltyTable';
import InvoiceBonusTable from './InvoiceBonusTable';
import { useAuth } from '../../../commons/Auth';
import generateYearOptions from '../../../lib/helpers/generateYearOptions';
import InsurancesRoyaltyTable from './InsurancesRoyaltyTable';
import InsurancesStudioRoyaltyTable from './InsurancesStudioRoyaltyTable';
import { ROYALTIES_ALLOWED_TYPES } from '../../../App';

const FinancialReportsPresentational = ({
  data = {},
  onBonusDelete,
  onBonusUpdate,
  query,
  onFilter,
  profileType,
  isProfileMaster,
}) => {
  const [{ isAdmin } = {}] = useAuth();
  const [t] = useTranslation('CONTACTS');
  const { categories = {}, totals = {} } = data;
  const {
    gnet_admin = {},
    supplier = {},
    bonuses = {},
    variableRevenues = {},
    variableRevenuesInsurances = {},
    variableRevenuesInsurancesStudio = {},
  } = categories;

  const { data: dataRev = [] } = variableRevenues;
  const { data: dataRevInsurances = [] } = variableRevenuesInsurances;
  const {
    data: dataRevInsurancesStudio = [],
  } = variableRevenuesInsurancesStudio;

  const getVariableRevenuesTotals = (dataRev) => {
    const cashedTotals = dataRev.reduce((prev, cur) => {
      const { payments } = cur || {};
      return (
        prev +
        (payments || []).reduce((acc, payment) => {
          return acc + ((payment && payment.gross) || 0);
        }, 0)
      );
    }, 0);
    const netTotals = dataRev.reduce((prev, cur) => prev + cur.net, 0);
    const grossTotals = dataRev.reduce((prev, cur) => prev + cur.gross, 0);
    const masterShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur.masterShare,
      0
    );
    const earnedMasterShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur.earnedMasterShare,
      0
    );
    const billableMasterShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur.billableMasterShare,
      0
    );
    return {
      cashedTotals,
      netTotals,
      grossTotals,
      masterShareTotals,
      earnedMasterShareTotals,
      billableMasterShareTotals,
    };
  };

  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      <TabContent activeTab={'1'}>
        <TabPane tabId="1">
          <ContFilterYear>
            <Row>
              <Col xs="6">
                <Label>{t('COMMON:TITLE_FILTER_ACC_YEAR')}</Label>
              </Col>
              <Col xs="6">
                <FormGroup>
                  <CustomInput
                    type="select"
                    id="selectYear"
                    name="year"
                    value={query && query.year}
                    onChange={({ target: { value: year } }) =>
                      onFilter({ year })
                    }
                  >
                    {generateYearOptions()}
                  </CustomInput>
                </FormGroup>
              </Col>
            </Row>
          </ContFilterYear>
          {ROYALTIES_ALLOWED_TYPES.includes(profileType) &&
            isProfileMaster && [
              <ContAffiliation key={0}>
                <Row>
                  <Col xs="6">
                    <H5Styled>{t('PROVIDERS')}</H5Styled>
                  </Col>
                </Row>
                <br />
                <ProvidersTable
                  data={[]}
                  truncateTable={true}
                  showEmptyTableMessage={false}
                  bottomRow={
                    <TotalsRow
                      activeRoute={true}
                      route={`suppliers${
                        query.year ? '?year=' + query.year : ''
                      }`}
                      totals={supplier.totals}
                      truncateTotals={true}
                    />
                  }
                  onFilter={() => {}}
                />
              </ContAffiliation>,

              <ContAffiliation key={1}>
                <Row>
                  <Col xs="6">
                    <H5Styled>{t('GNET_ADMINS')}</H5Styled>
                  </Col>
                  <Col xs="6" className="text-right" />
                </Row>
                <br />
                <AdministratorsTable
                  data={[]}
                  truncateTable={true}
                  bottomRow={
                    <TotalsRow
                      totals={gnet_admin.totals}
                      route={`gnet-admins${
                        query.year ? '?year=' + query.year : ''
                      }`}
                      activeRoute={true}
                      truncateTotals={true}
                    />
                  }
                  onFilter={() => {}}
                  showEmptyTableMessage={false}
                />
              </ContAffiliation>,

              <ContAffiliation key={2}>
                <Row>
                  <Col xs="6">
                    <H5Styled>{t('ROYALTY:TITLE')}</H5Styled>
                  </Col>
                  <Col xs="6" className="text-right" />
                </Row>
                <br />
                <br />
                <RoyaltyTable
                  data={[]}
                  truncateTable={true}
                  onFilter={() => {}}
                  bottomRow={
                    <TotalRevenuesRow
                      totals={getVariableRevenuesTotals(dataRev)}
                      route={`variable-revenues${
                        query.year ? '?year=' + query.year : ''
                      }`}
                      activeRoute={true}
                      truncateTotals={true}
                    />
                  }
                  showEmptyTableMessage={false}
                />
              </ContAffiliation>,

              <ContAffiliation key={3}>
                <Row>
                  <Col xs="6">
                    <H5Styled>{t('INSURANCES:ROYALTIES_TITLE')}</H5Styled>
                  </Col>
                  <Col xs="6" className="text-right" />
                </Row>
                <br />
                <br />
                <InsurancesRoyaltyTable
                  data={[]}
                  truncateTable={true}
                  onFilter={() => {}}
                  showEmptyTableMessage={false}
                  bottomRow={
                    <TotalInsuranceRevenuesRow
                      totals={getVariableRevenuesTotals(dataRevInsurances)}
                      route={`variable-revenues-insurances${
                        query.year ? '?year=' + query.year : ''
                      }`}
                      activeRoute={true}
                      truncateTotals={true}
                    />
                  }
                />
              </ContAffiliation>,
            ]}

          {ROYALTIES_ALLOWED_TYPES.includes(profileType) && (
            <ContAffiliation>
              <Row>
                <Col xs="6">
                  <H5Styled>{t('INSURANCES:STUDIO_ROYALTIES_TITLE')}</H5Styled>
                </Col>
                <Col xs="6" className="text-right" />
              </Row>
              <br />
              <br />
              <InsurancesStudioRoyaltyTable
                data={[]}
                onFilter={() => {}}
                truncateTable={true}
                showEmptyTableMessage={false}
                bottomRow={
                  <TotalInsuranceRevenuesRow
                    totals={getVariableRevenuesTotals(dataRevInsurancesStudio)}
                    route={`insurance-revenues-studio${
                      query.year ? '?year=' + query.year : ''
                    }`}
                    activeRoute={true}
                    truncateTotals={true}
                  />
                }
              />
            </ContAffiliation>
          )}

          <ContAffiliation>
            <Row>
              <Col xs="6">
                <H5Styled>{t('LABEL_INVOICE_BONUS')}</H5Styled>
              </Col>
            </Row>
            <br />
            <InvoiceBonusTable
              data={bonuses.data || []}
              handleDeleteBonus={onBonusDelete}
              handleUpdateBonus={onBonusUpdate}
              isAdmin={isAdmin}
              onFilter={() => {}}
            />
          </ContAffiliation>

          <ContAffiliation>
            <H5Styled style={{ marginBottom: '5px' }}>
              {t('COMMON:SUMMARY')}
            </H5Styled>
            <Summary
              variableRevenuesData={variableRevenues.data}
              variableRevenuesInsurancesData={variableRevenuesInsurances.data}
              totals={totals}
              supplierTotals={supplier.totals}
              gnetAdminTotals={gnet_admin.totals}
              profileType={profileType}
              isProfileMaster={isProfileMaster}
            />
          </ContAffiliation>
        </TabPane>
      </TabContent>
    </Form>
  );
};

export default FinancialReportsPresentational;
